import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search } from 'lucide-react';

export interface ScopeOption {
  id: number;
  label: string;
}

interface PromotionScopePickerProps {
  options: ScopeOption[];
  selectedIds: number[];
  onChange: (ids: number[]) => void;
  /** Rendered above the list when nothing matches the filter. */
  emptyMessage: string;
  invalid?: boolean;
}

/**
 * Filterable checkbox list used by the Create and Edit promotion pages to pick the
 * categories or products a rule is scoped to. Kept as a shared component because both
 * pages need identical selection behaviour over lists that can run to hundreds of rows.
 */
const PromotionScopePicker: React.FC<PromotionScopePickerProps> = ({
  options,
  selectedIds,
  onChange,
  emptyMessage,
  invalid = false,
}) => {
  const { t } = useTranslation();
  const [filter, setFilter] = useState('');

  const visibleOptions = useMemo(() => {
    const needle = filter.trim().toLowerCase();
    if (!needle) return options;
    return options.filter((option) => option.label.toLowerCase().includes(needle));
  }, [options, filter]);

  const toggle = (id: number) => {
    onChange(selectedIds.includes(id) ? selectedIds.filter((s) => s !== id) : [...selectedIds, id]);
  };

  return (
    <div className={`promotion-scope-picker ${invalid ? 'is-invalid' : ''}`}>
      <div className="promotion-scope-search">
        <Search size={14} strokeWidth={2} />
        <input
          type="text"
          className="form-input"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder={t('common.search')}
        />
        <span className="promotion-scope-count">
          {t('promotion.selectedCount', { count: selectedIds.length })}
        </span>
      </div>

      <div className="promotion-scope-list">
        {visibleOptions.length === 0 && <p className="promotion-scope-empty">{emptyMessage}</p>}
        {visibleOptions.map((option) => (
          <label key={option.id} className="promotion-scope-option">
            <input
              type="checkbox"
              checked={selectedIds.includes(option.id)}
              onChange={() => toggle(option.id)}
            />
            <span>{option.label}</span>
          </label>
        ))}
      </div>
    </div>
  );
};

export default PromotionScopePicker;
